import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { mcpServerUrl } from 'src/appConfig';
import { useGetMCPConnectorInfo } from 'src/content/applications/Users/settings/profile/hooks/useGetMCPConnectorInfo';
import { posthogService, PostHogEvent } from 'src/common/services/posthogService';

interface MCPModalProps {
  open: boolean;
  onClose: () => void;
}

const MCP_SERVER_URL = mcpServerUrl || 'https://mcp.seospace.co.uk';
const CLAUDE_DOCS_URL = 'https://modelcontextprotocol.io/quickstart/user';

const MCPModal = ({ open, onClose }: MCPModalProps) => {
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const { data: connectorInfo, isLoading: isConnectorLoading } =
    useGetMCPConnectorInfo();

  const isConnected = connectorInfo?.status === 'connected';

  useEffect(() => {
    if (open) {
      posthogService.track(PostHogEvent.CLAUDE_MCP_MODAL_OPENED, {
        status: connectorInfo?.status ?? 'disconnected'
      });
    }
  }, [open]);

  const handleClose = () => {
    posthogService.track(PostHogEvent.CLAUDE_MCP_MODAL_CLOSED, {
      status: connectorInfo?.status ?? 'disconnected',
    });
    onClose();
  };

  const mcpConfig = JSON.stringify(
    {
      mcpServers: {
        seospace: {
          url: MCP_SERVER_URL
        }
      }
    },
    null,
    2
  );

  const handleCopyConfig = async () => {
    posthogService.track(PostHogEvent.CLAUDE_MCP_CONFIG_COPIED, {
      status: connectorInfo?.status ?? 'disconnected'
    });
    try {
      await navigator.clipboard.writeText(mcpConfig);
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    } catch {
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = mcpConfig;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    }
  };

  const handleCopyUrl = async () => {
    posthogService.track(PostHogEvent.CLAUDE_MCP_SERVER_URL_COPIED, {
      status: connectorInfo?.status ?? 'disconnected'
    });
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = MCP_SERVER_URL;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const handleOpenDocs = () => {
    window.open(CLAUDE_DOCS_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      disableScrollLock={true}
      aria-labelledby="mcp-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          padding: { xs: '20px 16px', sm: '24px' }
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        id="mcp-dialog-title"
        sx={{
          p: 0,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '16px', sm: '18px' },
              lineHeight: '24px',
              color: 'var(--color-text-heading)'
            }}
          >
            Connect SEOSpace MCP Server
          </Typography>

          {/* Connection Status Badge */}
          {isConnectorLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.35,
                borderRadius: '16px',
                backgroundColor: isConnected
                  ? 'rgba(46, 125, 50, 0.08)'
                  : 'rgba(211, 47, 47, 0.08)',
                border: `1px solid ${isConnected
                    ? 'rgba(46, 125, 50, 0.3)'
                    : 'rgba(211, 47, 47, 0.3)'
                  }`
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  fontSize: 8,
                  color: isConnected ? '#2e7d32' : '#d32f2f'
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '11px',
                  lineHeight: '16px',
                  color: isConnected ? '#2e7d32' : '#d32f2f',
                  textTransform: 'capitalize'
                }}
              >
                {connectorInfo?.status ?? 'disconnected'}
              </Typography>
            </Box>
          )}
        </Box>

        <IconButton
          aria-label="close"
          size="small"
          onClick={handleClose}
          sx={{
            color: 'var(--color-text-secondary, #666)',
            '&:hover': {
              backgroundColor: 'var(--color-bg-light-purple, rgba(0, 0, 0, 0.04))'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={2.5}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--color-text-secondary, #666)',
              fontWeight: 500
            }}
          >
            Connect SEOSpace with Claude Desktop using the Model Context Protocol (MCP).
            This allows Claude to access your SEO data, run site audits, and provide
            AI-powered SEO recommendations directly in conversation.
          </Typography>

          {/* Connection Details */}
          {connectorInfo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary, #666)',
                  fontWeight: 500

                }}
              >
                Active connections:{' '}
                <strong>{connectorInfo.connections_count}</strong>
              </Typography>
              {connectorInfo.last_connected_at && (
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary, #666)',
                    fontWeight: 500

                  }}
                >
                  Last connected:{' '}
                  <strong>
                    {new Date(connectorInfo.last_connected_at).toLocaleString()}
                  </strong>
                </Typography>
              )}
            </Box>
          )}

          {/* MCP Server URL */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: 'var(--line-height-16)',
                color: 'var(--color-text-heading)',
                mb: 0.5
              }}
            >
              MCP Server URL
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'var(--color-bg-silver, #f5f5f5)',
                border: '1px solid var(--color-border, #e0e0e0)',
                borderRadius: '6px',
                px: 1.5,
                py: 0.75
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'var(--color-black)',
                  flex: 1,
                  wordBreak: 'break-all'
                }}
              >
                {MCP_SERVER_URL}
              </Typography>
              <Tooltip title={copiedUrl ? 'Copied!' : 'Copy URL'}>
                <IconButton size="small" onClick={handleCopyUrl}>
                  {copiedUrl ? (
                    <CheckIcon sx={{ fontSize: 16, color: 'green' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Claude Desktop Config */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: 'var(--line-height-16)',
                color: 'var(--color-text-heading)',
                mb: 0.5
              }}
            >
              Claude Desktop Configuration
            </Typography>
            <Box
              sx={{
                position: 'relative',
                background: 'var(--color-bg-silver, #f5f5f5)',
                border: '1px solid var(--color-border, #e0e0e0)',
                borderRadius: '6px',
                p: 1.5
              }}
            >
              <Box
                component="pre"
                sx={{
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: '12px',
                  lineHeight: 1.5,
                  color: 'var(--color-black)',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  overflowX: 'auto',
                  pr: 4
                }}
              >
                {mcpConfig}
              </Box>
              <Tooltip title={copiedConfig ? 'Copied!' : 'Copy config'}>
                <IconButton
                  size="small"
                  onClick={handleCopyConfig}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8
                  }}
                >
                  {copiedConfig ? (
                    <CheckIcon sx={{ fontSize: 16, color: 'green' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--color-text-secondary, #666)',
              fontWeight: 500

            }}
          >
            Paste the above JSON into your{' '}
            <b>Claude Desktop settings </b>
            under{' '}
            <b>Settings → Connectors → Yours → Add </b>
            to get started.
          </Typography>

          {/* Action Buttons */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={1.5}
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              onClick={handleOpenDocs}
              sx={{
                height: '38px',
                background: 'transparent',
                color: 'var(--color-primary)',
                borderColor: 'var(--color-primary)',
                textTransform: 'none',
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '13px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  borderColor: 'var(--color-primary)',
                  backgroundColor: 'var(--color-bg-light-purple, rgba(18, 2, 66, 0.04))'
                }
              }}
            >
              Setup Guide
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </Button>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                height: '38px',
                backgroundColor: 'var(--color-primary)',
                color: '#fff',
                textTransform: 'none',
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '13px',
                borderRadius: '6px',
                px: 3,
                '&:hover': {
                  backgroundColor: 'var(--color-primary-hover, #0e0135)'
                }
              }}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default MCPModal;
